/**
 * DeliveryShipments - Kanban/List view of all shipments
 * Mirrors the Repairs page pattern with status columns and detail panel.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../lib/api';
import {
  Package, Truck, Tag, CheckCircle2, Upload, Search,
  LayoutGrid, List, X, ChevronDown, MapPin, User,
  Calendar, Hash, Box, ArrowRight, Archive, MoreHorizontal,
  ChevronUp, ArrowUpDown
} from 'lucide-react';
import { DELIVERY_STATUSES, DELIVERY_STATUS_COLORS } from '../manifest';
import DeliveryImportModal from '../components/DeliveryImportModal';
import DeliveryShipmentDetail from '../components/DeliveryShipmentDetail';

const STATUS_ICONS = {
  'Imported':       <Package size={14} />,
  'Labels Printed': <Tag size={14} />,
  'In Transit':     <Truck size={14} />,
  'Delivered':      <CheckCircle2 size={14} />,
};

export default function DeliveryShipments() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'kanban' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImport, setShowImport] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [sortField, setSortField] = useState(null); // null | 'shipment_no' | 'delivery_no' | 'customer_name' | 'material_desc' | 'stop_no' | 'box_quantity' | 'status' | 'created_at'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

  const fetchShipments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/delivery/shipments');
      const filtered = (data || []).filter(s => !s.archived);
      // Debug: log geofence data for delivered shipments
      filtered.filter(s => s.status === 'Delivered').forEach(s => {
        console.log(`[DeliveryShipments] Shipment ${s.delivery_no || s.id}: status=${s.status}, geofence_inside=${s.geofence_inside} (type: ${typeof s.geofence_inside})`);
      });
      setShipments(filtered);
    } catch (err) {
      console.error('Error fetching shipments:', err);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShipments(); }, [fetchShipments]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    let result = shipments.filter(s => {
      const matchesSearch = !q ||
        (s.delivery_no || '').toLowerCase().includes(q) ||
        (s.shipment_no || '').toLowerCase().includes(q) ||
        (s.customer_name || '').toLowerCase().includes(q) ||
        (s.material_desc || '').toLowerCase().includes(q) ||
        (s.carrier_name || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    // Apply sorting
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle null/undefined values
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        // Handle different data types
        if (sortField === 'created_at') {
          // Date comparison
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        } else if (sortField === 'stop_no' || sortField === 'box_quantity') {
          // Numeric comparison
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          // String comparison
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [shipments, searchQuery, statusFilter, sortField, sortDirection]);

  const byStatus = useMemo(() => {
    const map = {};
    DELIVERY_STATUSES.forEach(st => { map[st] = []; });
    filtered.forEach(s => {
      if (map[s.status]) map[s.status].push(s);
    });
    return map;
  }, [filtered]);

  const updateStatus = async (shipment, newStatus) => {
    setUpdatingId(shipment.id);
    try {
      await api.patch(`/api/delivery/shipments/${shipment.id}/status`, { status: newStatus });
      setShipments(prev => prev.map(s => s.id === shipment.id ? { ...s, status: newStatus } : s));
      if (selectedShipment?.id === shipment.id) {
        setSelectedShipment(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const archiveShipment = async (shipment) => {
    if (!window.confirm(`Archive shipment ${shipment.delivery_no || shipment.id.slice(0, 8)}?`)) return;
    try {
      await api.patch(`/api/delivery/shipments/${shipment.id}/archive`);
      setShipments(prev => prev.filter(s => s.id !== shipment.id));
      if (selectedShipment?.id === shipment.id) setSelectedShipment(null);
    } catch (err) {
      console.error('Error archiving shipment:', err);
    }
  };

  const bg = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200 shadow-sm';

  return (
    <div className={`flex flex-col h-full overflow-hidden ${bg}`}>

      {/* Top bar */}
      <div className={`flex items-center gap-3 px-6 py-4 border-b flex-shrink-0 ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className={`flex items-center gap-2 flex-1 max-w-sm px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <Search size={14} className={isDark ? 'text-gray-400' : 'text-gray-400'} />
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search delivery #, customer, part..."
            className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>
              <X size={14} className="text-gray-400 hover:text-gray-200" />
            </button>
          )}
        </div>

        {/* Status filter tiles */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-violet-600 text-white'
                : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({shipments.length})
          </button>
          {DELIVERY_STATUSES.map(st => {
            const colors = DELIVERY_STATUS_COLORS[st];
            const count = shipments.filter(s => s.status === st).length;
            return (
              <button
                key={st}
                onClick={() => setStatusFilter(statusFilter === st ? 'all' : st)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  statusFilter === st
                    ? `${colors.bg} ${colors.border} ${colors.text}`
                    : isDark ? 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {st} ({count})
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* View toggle */}
          <div className={`flex rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 transition-colors ${viewMode === 'kanban' ? 'bg-violet-600 text-white' : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 transition-colors ${viewMode === 'list' ? 'bg-violet-600 text-white' : isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <List size={15} />
            </button>
          </div>

          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors text-sm"
          >
            <Upload size={14} />
            Import
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Kanban / List */}
        <div className={`flex-1 overflow-x-auto overflow-y-auto p-6 ${selectedShipment ? 'lg:flex-none lg:w-[calc(100%-420px)]' : ''}`}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanView
              byStatus={byStatus}
              isDark={isDark}
              selectedId={selectedShipment?.id}
              onSelect={setSelectedShipment}
              onStatusChange={updateStatus}
              onArchive={archiveShipment}
              updatingId={updatingId}
              onPrintLabels={(s) => navigate('/delivery/labels', { state: { shipmentId: s.id } })}
            />
          ) : (
            <ListView
              shipments={filtered}
              isDark={isDark}
              selectedId={selectedShipment?.id}
              onSelect={setSelectedShipment}
              onStatusChange={updateStatus}
              onArchive={archiveShipment}
              updatingId={updatingId}
              onPrintLabels={(s) => navigate('/delivery/labels', { state: { shipmentId: s.id } })}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={(field) => {
                if (sortField === field) {
                  setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                  setSortField(field);
                  setSortDirection('asc');
                }
              }}
            />
          )}
        </div>

        {/* Detail panel */}
        {selectedShipment && (
          <DeliveryShipmentDetail
            shipment={selectedShipment}
            isDark={isDark}
            onClose={() => setSelectedShipment(null)}
            onStatusChange={(newStatus) => updateStatus(selectedShipment, newStatus)}
            onArchive={() => archiveShipment(selectedShipment)}
            onPrintLabels={() => navigate('/delivery/labels', { state: { shipmentId: selectedShipment.id } })}
            updatingId={updatingId}
          />
        )}
      </div>

      {showImport && (
        <DeliveryImportModal
          isDark={isDark}
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchShipments(); }}
        />
      )}
    </div>
  );
}

/* ── Kanban View ─────────────────────────────────────────────────────────── */
function KanbanView({ byStatus, isDark, selectedId, onSelect, onStatusChange, onArchive, updatingId, onPrintLabels }) {
  return (
    <div className="flex gap-4 min-w-max pb-2">
      {DELIVERY_STATUSES.map(status => {
        const cards = byStatus[status] || [];
        const colors = DELIVERY_STATUS_COLORS[status];
        return (
          <div key={status} className="w-72 flex-shrink-0">
            {/* Column header */}
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl border-b-0 border ${colors.bg} ${colors.border} mb-0`}>
              <span className={colors.text}>{STATUS_ICONS[status]}</span>
              <span className={`text-sm font-semibold ${colors.text}`}>{status}</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-900/40 text-gray-300' : 'bg-white/60 text-gray-600'}`}>
                {cards.length}
              </span>
            </div>

            {/* Cards */}
            <div className={`rounded-b-xl border border-t-0 min-h-[200px] p-2 space-y-2 ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-gray-100/60 border-gray-200'}`}>
              {cards.length === 0 ? (
                <div className={`text-xs text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                  No shipments
                </div>
              ) : (
                cards.map(s => (
                  <ShipmentCard
                    key={s.id}
                    shipment={s}
                    isDark={isDark}
                    selected={selectedId === s.id}
                    onSelect={() => onSelect(s)}
                    onStatusChange={onStatusChange}
                    onArchive={onArchive}
                    onPrintLabels={onPrintLabels}
                    updating={updatingId === s.id}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── List View ───────────────────────────────────────────────────────────── */
function ListView({ shipments, isDark, selectedId, onSelect, onStatusChange, onArchive, updatingId, onPrintLabels, sortField, sortDirection, onSort }) {
  if (shipments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Package size={40} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No shipments found</p>
      </div>
    );
  }

  const SortableHeader = ({ field, children, className = '' }) => {
    const isActive = sortField === field;
    return (
      <div
        onClick={() => onSort(field)}
        className={`flex items-center gap-1.5 cursor-pointer select-none hover:opacity-80 transition-opacity ${className}`}
      >
        <span>{children}</span>
        {isActive ? (
          sortDirection === 'asc' ? (
            <ChevronUp size={12} className="flex-shrink-0" />
          ) : (
            <ChevronDown size={12} className="flex-shrink-0" />
          )
        ) : (
          <ArrowUpDown size={12} className="flex-shrink-0 opacity-40" />
        )}
      </div>
    );
  };

  return (
    <div className={`rounded-xl border overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200 shadow-sm'}`}>
      <div className="min-w-[1200px]">
        {/* Header row */}
        <div className={`grid grid-cols-12 gap-4 px-4 py-2.5 text-xs font-medium uppercase tracking-wider border-b ${isDark ? 'bg-gray-800/80 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
        <SortableHeader field="shipment_no" className="col-span-1 min-w-[80px]">Shipment #</SortableHeader>
        <SortableHeader field="delivery_no" className="col-span-1 min-w-[80px]">Delivery #</SortableHeader>
        <SortableHeader field="customer_name" className="col-span-2 min-w-[120px]">Customer</SortableHeader>
        <SortableHeader field="material_desc" className="col-span-2 min-w-[150px]">Part / Material</SortableHeader>
        <SortableHeader field="stop_no" className="col-span-1 min-w-[60px]">Stop</SortableHeader>
        <SortableHeader field="box_quantity" className="col-span-1 min-w-[60px]">Boxes</SortableHeader>
        <SortableHeader field="status" className="col-span-2 min-w-[120px]">Status</SortableHeader>
        <SortableHeader field="created_at" className="col-span-1 min-w-[80px]">Date</SortableHeader>
        <div className="col-span-1 min-w-[80px]">Actions</div>
        </div>

        {shipments.map(s => {
          const colors = DELIVERY_STATUS_COLORS[s.status] || DELIVERY_STATUS_COLORS['Imported'];
          // Check if delivery is outside geofence
          // geofence_inside can be: true, false, 'true', 'false', null, or undefined
          const geofenceValue = s.geofence_inside;
          const isOutsideGeofence = geofenceValue === false || geofenceValue === 'false' || geofenceValue === 0;
          const needsAttention = s.status === 'Delivered' && isOutsideGeofence;
          return (
            <div
              key={s.id}
            onClick={() => onSelect(s)}
            className={`grid grid-cols-12 gap-4 px-4 py-3 border-b cursor-pointer transition-colors ${
              needsAttention
                ? isDark ? 'bg-red-900/30 border-red-700/50' : 'bg-red-50 border-red-200'
                : selectedId === s.id
                  ? isDark ? 'bg-violet-900/20 border-violet-700/50' : 'bg-violet-50 border-violet-200'
                  : isDark ? 'border-gray-700/50 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'
            }`}
          >
            <div className={`col-span-1 text-sm font-medium truncate min-w-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {s.shipment_no || '—'}
            </div>
            <div className={`col-span-1 text-sm font-medium truncate min-w-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {s.delivery_no || '—'}
            </div>
            <div className={`col-span-2 text-sm truncate min-w-0 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {s.customer_name || '—'}
            </div>
            <div className={`col-span-2 text-sm truncate min-w-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {s.material_desc || '—'}
            </div>
            <div className={`col-span-1 text-sm truncate min-w-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {s.stop_no != null ? `#${s.stop_no}` : '—'}
            </div>
            <div className={`col-span-1 text-sm truncate min-w-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {s.box_quantity ?? '—'}
            </div>
            <div className="col-span-2 min-w-0">
              {needsAttention ? (
                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border bg-red-600 border-red-500 text-white">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-white" />
                  <span className="truncate">Needs Attention</span>
                </span>
              ) : (
                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${colors.bg} ${colors.border} ${colors.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                  <span className="truncate">{s.status}</span>
                </span>
              )}
            </div>
            <div className={`col-span-1 text-xs truncate min-w-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
            </div>
            <div className="col-span-1 flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => onPrintLabels(s)}
                className="p-1.5 rounded hover:bg-violet-600/20 text-violet-400 transition-colors"
                title="Print Labels"
              >
                <Tag size={13} />
              </button>
              <button
                onClick={() => onArchive(s)}
                className={`p-1.5 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}
                title="Archive"
              >
                <Archive size={13} />
              </button>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

/* ── Shipment Card (Kanban) ──────────────────────────────────────────────── */
function ShipmentCard({ shipment: s, isDark, selected, onSelect, onStatusChange, onArchive, onPrintLabels, updating }) {
  const [showMenu, setShowMenu] = useState(false);
  const colors = DELIVERY_STATUS_COLORS[s.status] || DELIVERY_STATUS_COLORS['Imported'];
  // Check if delivery is outside geofence
  // geofence_inside can be: true, false, 'true', 'false', null, or undefined
  const geofenceValue = s.geofence_inside;
  const isOutsideGeofence = geofenceValue === false || geofenceValue === 'false' || geofenceValue === 0;
  const needsAttention = s.status === 'Delivered' && isOutsideGeofence;

  const nextStatus = {
    'Imported':       'Labels Printed',
    'Labels Printed': 'In Transit',
    'In Transit':     'Delivered',
    'Delivered':      null,
  }[s.status];

  return (
    <div
      onClick={onSelect}
      className={`rounded-lg border p-3 cursor-pointer transition-all relative ${
        needsAttention
          ? isDark 
            ? 'border-red-500 bg-red-900/50 shadow-lg shadow-red-900/40' 
            : 'border-red-500 bg-red-100 shadow-lg shadow-red-200/40'
          : selected
            ? 'border-violet-500 bg-violet-900/20 shadow-lg shadow-violet-900/20'
            : isDark
              ? 'border-gray-700 bg-gray-800/60 hover:border-gray-600 hover:bg-gray-800'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {updating && (
        <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {s.delivery_no || s.shipment_no || s.id.slice(0, 8)}
          </div>
          {s.shipment_no && s.delivery_no && (
            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Ship: {s.shipment_no}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onPrintLabels(s)}
            className="p-1 rounded hover:bg-violet-600/20 text-violet-400 transition-colors"
            title="Print Labels"
          >
            <Tag size={12} />
          </button>
          <div className="relative">
            <button
              onClick={() => setShowMenu(v => !v)}
              className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`}
            >
              <MoreHorizontal size={12} />
            </button>
            {showMenu && (
              <div className={`absolute right-0 top-full mt-1 w-40 rounded-lg border shadow-xl py-1 z-20 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                {nextStatus && (
                  <button
                    onClick={() => { onStatusChange(s, nextStatus); setShowMenu(false); }}
                    className={`w-full px-3 py-2 text-left text-xs transition-colors ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    → Move to {nextStatus}
                  </button>
                )}
                <button
                  onClick={() => { onArchive(s); setShowMenu(false); }}
                  className={`w-full px-3 py-2 text-left text-xs transition-colors ${isDark ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-gray-50'}`}
                >
                  Archive
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status badge */}
      <div className="mt-2 mb-2">
        {needsAttention ? (
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border bg-red-600 border-red-500 text-white">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-white" />
            <span>Needs Attention</span>
          </span>
        ) : (
          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border ${colors.bg} ${colors.border} ${colors.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.dot}`} />
            <span>{s.status}</span>
          </span>
        )}
      </div>

      {/* Info rows */}
      <div className="space-y-1">
        {s.customer_name && (
          <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <User size={11} />
            <span className="truncate">{s.customer_name}</span>
          </div>
        )}
        {s.customer_address && (
          <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <MapPin size={11} />
            <span className="truncate">{s.customer_address}</span>
          </div>
        )}
        {s.material_desc && (
          <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Box size={11} />
            <span className="truncate">{s.material_desc}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={`flex items-center justify-between mt-2.5 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="flex items-center gap-2">
          {s.stop_no != null && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              Stop #{s.stop_no}
            </span>
          )}
          {s.box_quantity && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {s.box_quantity}
            </span>
          )}
        </div>
        <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}
        </span>
      </div>
    </div>
  );
}
